import { SetStateAction, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import "moment/locale/sv";
import { useTranslation } from "react-i18next";
import { TimeReportForStamping, TimeReportsForStamping } from "model/Company";
import { StampClockTeam } from "model/OrganizationalStructure";
import { TimeReportDto } from "model/TimeReport";
import { getSettingValue, isSettingEnabled } from "model/settings.logic";
import { DEFAULT_AUTO_CLOSE_DURATION_MS } from "components/designV2/autoCloseCountdown/autoCloseCountdown";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  buildDefaultStampSubmitData,
  type ClockStampSubmitData,
} from "./clockStampTimeReportPanel.logic";
import {
  isValidPersonalIdentityNumber,
  normalizePersonalIdentityNumber,
} from "web-apps/company/utils/personalIdentityNumber.utils";
import type { NumberPadInput } from "./numberPad.component";

const describeRequestError = (err: unknown): string =>
  axios.isAxiosError(err)
    ? `${err.response?.status ?? "no status"}: ${err.message}`
    : String(err);

export type StampResult = {
  checkInTime: string | null;
  checkOutTime: string | null;
};

const DEFAULT_TELEPHONE_PREFIX = "+46";
const SWEDISH_PHONE_REGEX = /^\+46\d{9}$/;
const LOOKUP_DEBOUNCE_MS = 600;

export type ClockStampModalState =
  | { type: "none" }
  | { type: "checkInOut" }
  | { type: "createAutoGeneratedGig" }
  | { type: "chooseTimeReport" }
  | { type: "timeReport"; showWarningModal: boolean };

const toTimeReportForStamping = (
  reports: TimeReportsForStamping,
): TimeReportForStamping => {
  const firstTimeReport = reports.timeReports[0];
  return {
    ...firstTimeReport,
    companyId: reports.companyId,
    workerPhoneNumber: reports.workerPhoneNumber,
  };
};

export const useClockStamp = () => {
  const { t, i18n } = useTranslation();
  const [companyState, companyActions] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();

  const identifierConfig = useMemo(() => {
    const isPersonalIdentityNumber =
      getSettingValue(
        companyState.companySettings,
        "CompanyTimeStampLoginMode",
      ) === "PersonalIdentityNumber";

    return {
      defaultValue: isPersonalIdentityNumber ? "" : DEFAULT_TELEPHONE_PREFIX,
      placeholderKey: isPersonalIdentityNumber
        ? "StampClock.PersonalIdentityNumberPlaceholder"
        : "StampClock.TelephoneNumber",
      isValid: (raw: string): boolean =>
        isPersonalIdentityNumber
          ? isValidPersonalIdentityNumber(raw)
          : SWEDISH_PHONE_REGEX.test(raw.trim()),
      toApiIdentifier: (raw: string): string =>
        isPersonalIdentityNumber
          ? normalizePersonalIdentityNumber(raw)
          : raw.trim().replace(/^\+/, ""),
    };
  }, [companyState.companySettings]);

  const [identifier, setIdentifier] = useState<string>(
    identifierConfig.defaultValue,
  );
  const [workerIdentifier, setWorkerIdentifier] = useState<string>("");
  const [userIsCheckedIn, setUserIsCheckedIn] = useState<boolean>(false);
  const [keyShouldBeDisabled, setKeyShouldBeDisabled] =
    useState<boolean>(false);
  const [modalState, setModalState] = useState<ClockStampModalState>({
    type: "none",
  });
  const [today, setToday] = useState<string>("");
  const [time, setTime] = useState<string>("");
  const [selectedWorkerTimeReport, setSelectedWorkerTimeReport] =
    useState<TimeReportForStamping>();
  const [workerTimeReports, setWorkerTimeReports] =
    useState<TimeReportsForStamping | null>(null);
  const [errorText, setErrorText] = useState("");
  const [isAutoGeneratedGig, setIsAutoGeneratedGig] = useState(false);
  const [stampResult, setStampResult] = useState<StampResult | null>(null);
  const [timeReportDetails, setTimeReportDetails] =
    useState<TimeReportDto | null>(null);
  const [teams, setTeams] = useState<StampClockTeam[]>([]);
  const [isTimeReportLoading, setIsTimeReportLoading] = useState(false);
  const [isSubmittingTimeReport, setIsSubmittingTimeReport] = useState(false);

  const setSimpleModalOpen =
    (modalType: "checkInOut" | "createAutoGeneratedGig" | "chooseTimeReport") =>
    (value: SetStateAction<boolean>) => {
      setModalState((prev) => {
        const isOpen = prev.type === modalType;
        const nextIsOpen = typeof value === "function" ? value(isOpen) : value;

        if (nextIsOpen) {
          return { type: modalType };
        }

        if (isOpen) {
          return { type: "none" };
        }

        return prev;
      });
    };

  const setTimeReportModalOpen = (value: SetStateAction<boolean>) => {
    setModalState((prev) => {
      const isOpen = prev.type === "timeReport";
      const nextIsOpen = typeof value === "function" ? value(isOpen) : value;

      if (nextIsOpen) {
        return { type: "timeReport", showWarningModal: false };
      }

      if (isOpen) {
        return { type: "none" };
      }

      return prev;
    });
  };

  const setWarningModalOpen = (value: SetStateAction<boolean>) => {
    setModalState((prev) => {
      if (prev.type !== "timeReport") {
        return prev;
      }

      const nextIsOpen =
        typeof value === "function" ? value(prev.showWarningModal) : value;

      return { ...prev, showWarningModal: nextIsOpen };
    });
  };

  const updateTime = () => {
    const lang = (i18n.language || "sv").split("-")[0];
    const currentDate = moment().tz("Europe/Stockholm").locale(lang);

    setTime(currentDate.format("HH:mm"));
    setToday(
      currentDate.format("dddd D MMMM").replace(/^\w/, (c) => c.toUpperCase()),
    );
  };

  const clearInputs = () => {
    setIdentifier(identifierConfig.defaultValue);
    setErrorText("");
    setSelectedWorkerTimeReport(undefined);
    setKeyShouldBeDisabled(false);
  };

  const onClickedC = () => {
    setIdentifier(identifierConfig.defaultValue);
    setErrorText("");
    setKeyShouldBeDisabled(false);
  };

  const showTimeReportErrorAlert = () => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("Alerts.SomethingWentWrong"),
      },
    });
  };

  const fetchStampClockTimeReport = async (
    companyId: number,
    workerIdentifier: string,
    timeReportId: number,
  ): Promise<TimeReportDto | null> => {
    try {
      const res = await Api().company.getStampClockTimeReport(
        companyId,
        workerIdentifier,
        timeReportId,
      );
      return res.data;
    } catch (err) {
      console.error("Error fetching time report:", describeRequestError(err));
      return null;
    }
  };

  const doTheCheckInAndOut = async (
    companyId: number,
    timeReportId: number,
    workerIdentifier: string,
  ): Promise<void> => {
    try {
      const res = await companyActions.handleCheckInCheckOut(
        companyId,
        timeReportId,
        workerIdentifier,
      );
      const updated = res?.active;
      setStampResult(
        updated
          ? {
              checkInTime: updated.checkInTime ?? null,
              checkOutTime: updated.checkOutTime ?? null,
            }
          : null,
      );
      setModalState({ type: "checkInOut" });
      setIdentifier(identifierConfig.defaultValue);
      setKeyShouldBeDisabled(false);

      // The check-out modal previews the values the time report will be
      // submitted with, so the report is loaded as soon as it is stamped out.
      if (updated?.checkOutTime) {
        setIsTimeReportLoading(true);
        const fetchedTimeReport = await fetchStampClockTimeReport(
          companyId,
          workerIdentifier,
          timeReportId,
        );
        setTimeReportDetails(fetchedTimeReport);
        setIsTimeReportLoading(false);
        if (!fetchedTimeReport) {
          showTimeReportErrorAlert();
        }
      }
    } catch {
      console.log("problem checkin and out");
    }
  };

  const handleCheckInCheckOut = () => {
    setSimpleModalOpen("chooseTimeReport")(false);
    if (!selectedWorkerTimeReport) return;

    const { timeReportId, checkInTime, checkOutTime, companyId } =
      selectedWorkerTimeReport;

    const hasOpenSide = !checkInTime || !checkOutTime;
    if (!timeReportId || !companyId || !workerIdentifier || !hasOpenSide)
      return;

    doTheCheckInAndOut(companyId, timeReportId, workerIdentifier);
  };

  const timeReportDefaultSubmitData = useMemo(
    () =>
      timeReportDetails
        ? buildDefaultStampSubmitData(
            timeReportDetails,
            companyState.companySettings,
          )
        : null,
    [timeReportDetails, companyState.companySettings],
  );

  const handleEditTimeReport = () => {
    if (!timeReportDetails) return;
    setTimeReportModalOpen(true);
  };

  const submitTimeReport = async (data: ClockStampSubmitData) => {
    if (isSubmittingTimeReport || !timeReportDetails?.id) return;

    const companyId = companyState.company?.id;
    if (!companyId) return;

    setIsSubmittingTimeReport(true);
    try {
      await Api().company.clockStampTimeReport(
        companyId,
        workerIdentifier,
        timeReportDetails.id,
        data,
      );
      setIsSubmittingTimeReport(false);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          title: t("WorkerTimeReport.TimeReported"),
          message: "",
          autoCloseMs: DEFAULT_AUTO_CLOSE_DURATION_MS,
        },
      });
      setModalState({ type: "none" });
      setTimeReportDetails(null);
      setWorkerIdentifier("");
      clearInputs();
    } catch (err) {
      console.error("Error submitting time report:", describeRequestError(err));
      setIsSubmittingTimeReport(false);
      showTimeReportErrorAlert();
    }
  };

  const handleSubmitDefaultTimeReport = async () => {
    if (!timeReportDefaultSubmitData) return;
    await submitTimeReport(timeReportDefaultSubmitData);
  };

  const getWorkerTimeReport = async () => {
    const rawIdentifier = (identifier || "").trim();
    const companyId = companyState.company?.id;
    if (!identifierConfig.isValid(rawIdentifier) || !companyId) return;

    const apiIdentifier = identifierConfig.toApiIdentifier(rawIdentifier);

    setWorkerIdentifier("");
    setModalState((prev) => {
      if (prev.type === "chooseTimeReport" || prev.type === "checkInOut") {
        return { type: "none" };
      }
      return prev;
    });
    setIsAutoGeneratedGig(false);
    setSelectedWorkerTimeReport(undefined);
    setTimeReportDetails(null);
    setStampResult(null);
    setWorkerTimeReports(null);
    setUserIsCheckedIn(false);

    let shouldOpenAutoGig = false;

    try {
      const res = await companyActions.getAvailableTimeReportsForStamping(
        companyId,
        apiIdentifier,
      );
      setWorkerTimeReports(res);
    } catch (err: any) {
      const msg = err?.response?.data?.errorMessage;

      if (msg === "No matching time report found") {
        try {
          const [allowGenerateRes, teamsRes] = await Promise.all([
            Api().company.getSettingForAllowingAutoGeneratedGig(companyId),
            Api().company.getStampClockWorkerWorkgroups(
              companyId,
              apiIdentifier,
            ),
          ]);
          setTeams(teamsRes.data);
          if (allowGenerateRes.data && teamsRes.data.length >= 1) {
            shouldOpenAutoGig = true;
          } else {
            setErrorText(t("StampClock.NoOpenTimeReports"));
          }
        } catch (e) {
          console.error("Failed to check auto-generate setting:", e);
          setErrorText(t("StampClock.NoOpenTimeReports"));
        }
      } else if (msg === "Worker not found") {
        setErrorText(t("StampClock.NoNumber"));
      } else if (msg === "Invalid or missing ssn") {
        setErrorText("StampClock.InvalidOrMissingSSN");
      } else {
        setErrorText(t("StampClock.NoOpenTimeReports"));
      }
    } finally {
      setWorkerIdentifier(apiIdentifier);

      if (shouldOpenAutoGig) {
        setModalState({ type: "createAutoGeneratedGig" });
      }
    }
  };

  const handleNumberPadInput = (input: NumberPadInput) => {
    if (input.type === "clear") {
      onClickedC();
      return;
    }

    setIdentifier(identifier + input.value);
  };

  useEffect(() => {
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIdentifier(identifierConfig.defaultValue);
  }, [identifierConfig.defaultValue]);

  useEffect(() => {
    setErrorText("");

    if (!identifierConfig.isValid(identifier)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      getWorkerTimeReport();
    }, LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, companyState.company?.id]);

  useEffect(() => {
    if (workerTimeReports) {
      if (workerTimeReports.timeReports.length > 1) {
        setSelectedWorkerTimeReport(undefined);
        setModalState({ type: "chooseTimeReport" });
      } else {
        setSelectedWorkerTimeReport(toTimeReportForStamping(workerTimeReports));
      }
    }
  }, [workerTimeReports]);

  useEffect(() => {
    if (selectedWorkerTimeReport) {
      setUserIsCheckedIn(
        !!(
          selectedWorkerTimeReport.checkInTime &&
          !selectedWorkerTimeReport.checkOutTime
        ),
      );
    } else {
      setUserIsCheckedIn(false);
    }
    setKeyShouldBeDisabled(
      identifierConfig.isValid(identifier) && !!selectedWorkerTimeReport,
    );
  }, [selectedWorkerTimeReport, identifier, identifierConfig]);

  const haveStampClockModule = useMemo(() => {
    return isSettingEnabled(
      companyState.companySettings,
      "CompanyShowStampclockModule",
    );
  }, [companyState.companySettings]);

  return {
    t,
    identifier,
    setIdentifier,
    identifierPlaceholderKey: identifierConfig.placeholderKey,
    userIsCheckedIn,
    keyShouldBeDisabled,
    modalState,
    today,
    time,
    selectedWorkerTimeReport,
    setSelectedWorkerTimeReport,
    workerTimeReports,
    errorText,
    isAutoGeneratedGig,
    setIsAutoGeneratedGig,
    timeReportDetails,
    setTimeReportDetails,
    stampResult,
    teams,
    timeReportDefaultSubmitData,
    isTimeReportLoading,
    isSubmittingTimeReport,
    workerIdentifier,
    setWorkerIdentifier,
    setKeyShouldBeDisabled,
    setSimpleModalOpen,
    setTimeReportModalOpen,
    setWarningModalOpen,
    clearInputs,
    handleCheckInCheckOut,
    handleEditTimeReport,
    handleSubmitDefaultTimeReport,
    submitTimeReport,
    handleNumberPadInput,
    haveStampClockModule,
  };
};
